/**
 * The guest list as names you can paste.
 *
 * The CSV export answers a different question. It carries every column and the
 * ids that let it be imported back, which is what makes it useless for the
 * thing a host actually needs at a venue: handing over who is coming. Nobody
 * pastes a spreadsheet into a text message to a restaurant.
 *
 * So this is names, one per line, and nothing else — no header, no quoting, no
 * ids. It goes into a text, a WhatsApp message, an email to the maitre d', or
 * the top of a document, and it arrives looking like a list of people.
 */
import type { TrackerGuest } from '../services/tracker-api-client';

export type NameListScope = 'here' | 'going' | 'everyone';

export const NAME_LIST_SCOPES: ReadonlyArray<{ value: NameListScope; label: string; hint: string }> = [
  { value: 'here', label: 'Names of who is here tonight', hint: 'Coming, and in town on tonight’s date. For a door list.' },
  { value: 'going', label: 'Names of who is coming', hint: 'Everyone attending, whatever night they travel.' },
  { value: 'everyone', label: 'Names of everyone invited', hint: 'Including declines and no-replies.' },
];

export interface NameList {
  /** What goes on the clipboard. One name per line, no trailing newline. */
  text: string;
  /** How many names are in it. */
  count: number;
  /**
   * Rows that matched the scope but carry no usable name.
   *
   * Reported rather than silently dropped: a host who copies 38 names for a
   * party of 40 has to be told why, or the list quietly under-counts the room.
   */
  unnamed: number;
  /**
   * On the `here` scope: names included despite having no travel dates on file.
   *
   * They are included, not excluded, and the count says so. A roster routinely
   * carries guests whose flights were never entered, and the failure modes are
   * not symmetric: an extra name on a door list is nothing, while a missing one
   * is somebody standing outside a club at midnight arguing with a bouncer.
   */
  assumed: number;
}

/**
 * Is this guest coming?
 *
 * Reads `going`, the field the Guests tab itself displays, NOT `rsvpStatus`.
 * The two genuinely disagree: the Going / Can't Go dropdown writes
 * `goingOverride`, and `going` is the resolved answer that accounts for it,
 * while `rsvpStatus` is only what the guest themself last said. A host who has
 * marked somebody as coming expects them in this list.
 */
function isGoing(guest: Pick<TrackerGuest, 'going' | 'rsvpStatus'>): boolean {
  const resolved = String(guest.going ?? '').trim().toLowerCase();
  if (resolved) return resolved === 'going' || resolved === 'yes' || resolved === 'true';
  return String(guest.rsvpStatus ?? '').trim().toLowerCase() === 'going';
}

/** Collapse the whitespace a pasted-in roster carries, without touching the name itself. */
function cleanName(raw: unknown): string {
  return String(raw ?? '').replace(/\s+/g, ' ').trim();
}

/** `YYYY-MM-DD` off a date field, ignoring any time and zone that came with it. */
function dayOf(raw: unknown): string {
  const value = String(raw ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
}

/**
 * Is this guest in town on `day`?
 *
 * Compared as `YYYY-MM-DD` strings, which sort correctly and — more importantly
 * — carry no timezone. Parsing these into Date objects is how "arrives the
 * 15th" becomes the 14th for a host whose browser is west of the venue.
 *
 * Departure day counts as present: somebody flying out Sunday is at Saturday
 * night's party. A missing date is not evidence of absence, so a guest with
 * neither is included and reported separately.
 */
function isHereOn(guest: Pick<TrackerGuest, 'arrivalDate' | 'departureDate'>, day: string): boolean {
  const arrival = dayOf(guest.arrivalDate);
  const departure = dayOf(guest.departureDate);
  if (arrival && day < arrival) return false;
  if (departure && day > departure) return false;
  return true;
}

function hasTravelDates(guest: Pick<TrackerGuest, 'arrivalDate' | 'departureDate'>): boolean {
  return Boolean(dayOf(guest.arrivalDate) || dayOf(guest.departureDate));
}

export function guestNameList(
  guests: ReadonlyArray<TrackerGuest> | null | undefined,
  scope: NameListScope,
  /** The night in question as `YYYY-MM-DD`. Only read by the `here` scope. */
  day = '',
): NameList {
  const matching = (guests ?? []).filter((guest) => {
    if (scope === 'everyone') return true;
    if (!isGoing(guest)) return false;
    // With no day to compare against, `here` can only mean "coming".
    return scope !== 'here' || !day || isHereOn(guest, day);
  });

  const names: string[] = [];
  let unnamed = 0;
  let assumed = 0;

  for (const guest of matching) {
    const name = cleanName(guest.fullName);
    // A blank line in a pasted list reads as a missing person rather than as a
    // row with no name, so an unnamed row is counted and left out.
    if (!name) {
      unnamed += 1;
      continue;
    }
    names.push(name);
    if (scope === 'here' && day && !hasTravelDates(guest)) assumed += 1;
  }

  return { text: names.join('\n'), count: names.length, unnamed, assumed };
}

/** What to tell the host after the copy, including the parts they cannot see. */
export function nameListNotice(list: NameList, scope: NameListScope): string {
  if (list.count === 0) {
    if (scope === 'here') return 'Nobody on the list is marked as in town that night.';
    return scope === 'going'
      ? 'Nobody is marked as coming yet, so there was nothing to copy.'
      : 'There is nobody on the guest list yet.';
  }

  const who = scope === 'here' ? 'here that night' : scope === 'going' ? 'coming' : 'invited';
  const parts = [`Copied ${list.count} ${list.count === 1 ? 'name' : 'names'} — everyone ${who}.`];

  if (list.assumed > 0) {
    parts.push(
      `${list.assumed} of them ${list.assumed === 1 ? 'has' : 'have'} no travel dates on file`
      + ' and were included anyway.',
    );
  }
  if (list.unnamed > 0) {
    parts.push(
      `${list.unnamed} more ${list.unnamed === 1 ? 'row has' : 'rows have'} no name on file`
      + ` and ${list.unnamed === 1 ? 'was' : 'were'} left out.`,
    );
  }
  return parts.join(' ');
}
